import * as React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Checkbox,
  FieldError,
  FlexRow,
  Form,
  IconCheckRounded,
  Input,
  Label,
  Legend,
  Link,
  LoadingSpinner,
  RequiredText,
  Select,
  Stack,
  TagCheckbox,
  Text,
  TextArea,
  UnexpectedError,
} from "atoms";
import { ImageUpload } from "features/images";
import { useAuth } from "features/auth";
import { AffiliatedReviewableInput } from "features/search";
import { fieldOptions, validators } from "utils/form";
import { LinksInput } from "features/misc";
import { useArtistForm } from "../api/useArtistForm";
import type { Artist, ArtistFormFields } from "../types";
import { artistAttributes, artistMediums, countries } from "../data";
import { ArtistCollectionsInput } from "./ArtistCollectionsInput";

const textFieldMaxLength = 100;
const textFieldRequirements = {
  ...fieldOptions.required,
  ...fieldOptions.maxLength(textFieldMaxLength),
};

const textAreaMaxLength = 2000;
const textAreaRequirements = {
  ...fieldOptions.required,
  ...fieldOptions.maxLength(textAreaMaxLength),
};

type Props = {
  artist?: Artist;
};

function ArtistForm({ artist }: Props) {
  const {
    roles: { showAdminFeatures },
  } = useAuth();

  const {
    control,
    formState: { errors, isDirty },
    isEdit,
    mutation: { data, isSuccess, error, isError, isLoading: isArtistUpdating },
    onSubmit,
    register,
    setValue,
    setFocus,
  } = useArtistForm(artist);

  const isDuplicateId =
    error instanceof Error && error?.message.includes("ID Already Used");
  const showStatusField = artist?.status === "DRAFT" || showAdminFeatures;

  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const showSuccessDuration = 7;

  // These two individuals have more than 25 collections
  const maxCollections =
    artist?.id === "jay_greathouse" || artist?.id === "artist_villian"
      ? 33
      : 25;

  React.useEffect(() => {
    if (isSuccess) {
      setShowSuccessMessage(true);

      const timeout = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 1000 * showSuccessDuration);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [isSuccess]);

  React.useEffect(() => {
    if (isDuplicateId) {
      setFocus("id");
    }
  }, [isDuplicateId, setFocus]);

  return (
    <SForm onSubmit={onSubmit}>
      <Box fullWidth>
        <Stack gap="lg">
          {!isEdit && (
            <Label>
              Artist ID / SLUG
              <Input
                {...register("id", {
                  ...textFieldRequirements,
                  validate: validators.validSlug,
                })}
                maxLength={textFieldMaxLength}
                isInvalid={!!errors.id}
                aria-invalid={!!errors.id}
              />
              {errors.id && <FieldError>{errors.id.message}</FieldError>}
              {isDuplicateId && (
                <FieldError>
                  Sorry, that Artist ID already exists. Choose another and
                  resubmit.
                </FieldError>
              )}
            </Label>
          )}

          <Label>
            Artist Name
            <RequiredText />
            <Input
              {...register("name", textFieldRequirements)}
              maxLength={textFieldMaxLength}
              isInvalid={!!errors.name}
              aria-invalid={!!errors.name}
            />
            {errors.name && <FieldError>{errors.name.message}</FieldError>}
          </Label>

          <Label>
            Country of Residence
            <Select {...register("country")}>
              <option value="">Not Specified</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Select>
          </Label>

          <Label>
            City of Residence
            <Input
              {...register("city", fieldOptions.maxLength(textFieldMaxLength))}
            />
            {errors.city && <FieldError>{errors.city.message}</FieldError>}
          </Label>

          <Label>
            Pronouns
            <Input
              {...register(
                "pronouns",
                fieldOptions.maxLength(textFieldMaxLength)
              )}
            />
            {errors.pronouns && (
              <FieldError>{errors.pronouns.message}</FieldError>
            )}
          </Label>

          <div>
            <Text bold>
              Profile Image
              <RequiredText />
            </Text>

            <ImageUpload
              register={register}
              setValue={setValue}
              fieldName="heroImage"
              required
              src={artist?.heroImage}
              errorMessage={errors.heroImage?.url?.message}
              aspect="square"
              entityType="ARTIST"
            />
          </div>

          <div>
            <Text bold>Banner Image</Text>

            <ImageUpload
              register={register}
              setValue={setValue}
              fieldName="bannerImage"
              src={artist?.bannerImage}
              errorMessage={errors.bannerImage?.url?.message}
              aspect="banner"
              previewWidth="100%"
            />
          </div>

          <Label as="fieldset">
            <Legend>
              Choose tags to describe{" "}
              {showAdminFeatures ? "the artist's" : "your"} medium
            </Legend>
            {artistMediums.map((value) => (
              <TagCheckbox
                {...register("medium")}
                key={value}
                variant="medium"
                label={value}
                value={value}
              />
            ))}
            {errors.medium && (
              <FieldError block>{errors.medium.message}</FieldError>
            )}
          </Label>

          <Label as="fieldset">
            <Legend>
              Choose tags to describe{" "}
              {showAdminFeatures ? "the artist" : "yourself"}
            </Legend>
            {artistAttributes.map((value) => (
              <TagCheckbox
                {...register("founderAttributes")}
                key={value}
                variant="founderAttribute"
                label={value}
                value={value}
              />
            ))}
            {errors.founderAttributes && (
              <FieldError block>{errors.founderAttributes.message}</FieldError>
            )}
          </Label>

          <Label>
            Bio
            <RequiredText />
            <TextArea
              {...register("description", textAreaRequirements)}
              maxLength={textAreaMaxLength}
              isInvalid={!!errors.description}
              aria-invalid={!!errors.description}
              style={{ minHeight: 200 }}
            />
            {errors.description && (
              <FieldError>{errors.description.message}</FieldError>
            )}
          </Label>
          <div>
            <Checkbox
              {...register("acceptsCommissions")}
              label={
                <strong>Allow collectors to contact you for commissions</strong>
              }
              style={{ fontWeight: "bold" }}
              aria-describedby="#commissions-description"
            />
            <Text
              size="xxs"
              id="commissions-description"
              style={{ marginTop: 10 }}
            >
              Opting in to communications does not obligate you to accept any
              offer that is not a good fit. You have the right to accept or
              decline any requests you receive. You can opt in and opt out of
              this feature at anytime.
            </Text>
          </div>

          <LinksInput<ArtistFormFields>
            control={control}
            errors={errors}
            register={register}
            name="links"
          />

          {showAdminFeatures && (
            <Label>
              YouTube Video ID&nbsp;
              <small>e.g. - vozDHPafnYE</small>
              <Input
                {...register("youTubeVideo")}
                isInvalid={!!errors.youTubeVideo}
                aria-invalid={!!errors.youTubeVideo}
              />
              {errors.youTubeVideo && (
                <FieldError>{errors.youTubeVideo.message}</FieldError>
              )}
            </Label>
          )}

          <ArtistCollectionsInput
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            maxCollections={maxCollections}
          />

          {showAdminFeatures && (
            <fieldset>
              <Legend>Choose Affiliated Projects</Legend>

              <AffiliatedReviewableInput
                control={control}
                name="affiliations"
                editingId={artist?.id}
                typeLabel="Projects"
              />
            </fieldset>
          )}

          {showStatusField && (
            <Label>
              Status
              <RequiredText />
              <Select {...register("status", { ...fieldOptions.required })}>
                <option value="PUBLISHED">Published</option>
                <option value="DRAFT">Draft</option>
                {showAdminFeatures && (
                  <option value="ARCHIVED">Archived</option>
                )}
              </Select>
            </Label>
          )}
        </Stack>
      </Box>

      <FormFooter>
        {showSuccessMessage && !isDirty && (
          <SuccessMessage duration={showSuccessDuration} aria-live="polite">
            {/* Make disappear after a few seconds */}
            <IconCheckRounded />
            Your changes have been saved
          </SuccessMessage>
        )}

        {isError && <UnexpectedError error={error} />}

        <FlexRow
          justifyContent="space-between"
          flexWrap="nowrap"
          gap="1em 5px"
          style={{ width: "100%" }}
        >
          {!artist && !data ? (
            <div />
          ) : (
            <Link
              variant="secondary"
              size="sm"
              to={`/artists/${artist?.id ?? data?.id}`}
              style={{ margin: 0 }}
            >
              {isDirty ? "Cancel" : "View Profile"}
            </Link>
          )}

          <Button
            type="submit"
            variant="primary"
            size="sm"
            arrow={isEdit ? undefined : "r"}
            disabled={isArtistUpdating}
          >
            {isEdit ? "Save Changes" : "Submit Artist"}
          </Button>
        </FlexRow>
      </FormFooter>
      {isArtistUpdating && <LoadingSpinner />}
    </SForm>
  );
}

const SForm = styled(Form)(({ theme }) => ({
  gap: "3rem",
  gridArea: "b",
  maxWidth: "750px",
  margin: "0 auto",
  h2: {
    ...theme.fonts.body,
    fontWeight: "bold",
  },
}));

const FormFooter = styled.footer(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
  backgroundColor: theme.colors.bg,
  position: "sticky",
  bottom: 0,
  background: theme.colors.bg,
  padding: `1em 0 calc(1em + env(safe-area-inset-bottom))`,
  zIndex: 10,
  "&>*": {
    position: "relative",
    zIndex: 3,
  },
  "&::before, &::after": {
    backgroundColor: theme.colors.bg,
    content: "''",
    position: "absolute",
    top: 0,
    left: "50%",
    width: "100vw",
    bottom: 0,
    transform: "translateX(-50%)",
    zIndex: 2,
  },
  "&::before": {
    bottom: "50%",
    boxShadow: theme.boxShadow.light,
    zIndex: 1,
  },
}));

const messageInOut = keyframes({
  "0%, 100%": {
    opacity: 0,
    transform: "translate(-50%, 100%)",
  },
  "5%, 95%": {
    opacity: 1,
    transform: "translate(-50%, 0)",
  },
});

const SuccessMessage = styled.div<{ duration: number }>(
  ({ duration, theme }) => ({
    display: "flex",
    gap: 5,
    alignItems: "center",
    backgroundColor: theme.colors.green,
    borderRadius: theme.borderRadius.sm,
    border: `2px solid ${theme.colors.greenDark}`,
    boxShadow: theme.boxShadow.dark,
    color: theme.colors.fg,
    fontSize: theme.fontSizes.xxs,
    lineHeight: 1.2,
    left: "50%",
    marginBottom: 0,
    padding: ".4em 1em .5em",
    position: "absolute",
    pointerEvents: "none",
    bottom: "calc(100% + 1rem)",
    zIndex: 99,
    animation: `${duration}s ease 0s forwards ${messageInOut}`,
    svg: {
      width: 13,
    },
    "@media (min-width: 660px)": {
      bottom: "auto",
    },
  })
);

export { ArtistForm, FormFooter, SuccessMessage };
